import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Save, Plus, Trash2, BookOpen, X } from "lucide-react";
import { toast } from "sonner";

const SECTION_LABELS = {
  logo: "Logotipo",
  colors: "Paleta de colores",
  typography: "Tipografía",
  visual_style: "Estilo visual",
  voice: "Tono de voz",
  personality: "Personalidad de marca",
  promise: "Promesa de marca",
  differentiators: "Diferenciadores",
  claims_permitted: "Claims permitidos",
  claims_forbidden: "Claims prohibidos",
  awareness_levels: "Niveles de conciencia",
  customer_avatar: "Avatar cliente ideal",
  objections: "Objeciones",
  desires: "Deseos",
  hooks_approved: "Ganchos aprobados",
  ctas: "CTAs aprobados",
  permitted_words: "Palabras permitidas",
  forbidden_words: "Palabras prohibidas",
  social_proof: "Pruebas sociales",
};

const BrandKit = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/brand-kit").then((r) => setSections(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateSection = async (id, patch) => {
    await api.patch(`/brand-kit/${id}`, patch);
    toast.success("Guardado");
    load();
  };

  return (
    <Layout
      title="Brand Kit Rubí"
      subtitle="ADN visual y verbal de la marca"
      actions={
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-section-btn">
          <Plus size={14} /> Nueva sección
        </button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="brand-grid">
          {sections.map((s) => (
            <BrandSectionCard key={s.id} section={s} onSave={(patch) => updateSection(s.id, patch)} onDelete={async () => {
              if (!window.confirm("¿Eliminar sección?")) return;
              await api.delete(`/brand-kit/${s.id}`);
              load();
            }} />
          ))}
        </div>
      )}

      {showForm && <NewSection onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
    </Layout>
  );
};

const BrandSectionCard = ({ section, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(section.content || "");
  const [items, setItems] = useState(section.items || []);
  const [newItem, setNewItem] = useState("");

  const addItem = () => {
    if (!newItem.trim()) return;
    setItems([...items, { name: newItem.trim() }]);
    setNewItem("");
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const save = () => {
    onSave({ content, items });
    setEditing(false);
  };

  const isColors = section.section === "colors";

  return (
    <div className="aura-card p-6" data-testid={`section-${section.section}`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="label-overline">{section.section}</div>
          <h3 className="font-display text-lg font-medium mt-1">{section.title}</h3>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditing(!editing)} className="btn-ghost text-xs">
            {editing ? "Cerrar" : "Editar"}
          </button>
          <button onClick={onDelete} className="text-[#666] hover:text-red-600 p-1"><Trash2 size={14} /></button>
        </div>
      </div>

      {editing ? (
        <div className="space-y-3">
          <textarea className="aura-input" rows="3" value={content} onChange={(e) => setContent(e.target.value)} />
          <div>
            <div className="label-overline mb-1">Items</div>
            <div className="flex flex-wrap gap-2 mb-2">
              {items.map((it, i) => (
                <span key={i} className="pill pill-yellow flex items-center gap-1">
                  {isColors && it.hex && <span className="w-3 h-3 rounded" style={{ background: it.hex }} />}
                  {it.name || it.hex} <button type="button" onClick={() => removeItem(i)}><X size={10} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input className="aura-input text-sm" value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder={isColors ? "#D99D26" : "Item"} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem())} />
              <button type="button" onClick={addItem} className="btn-outline text-sm">Agregar</button>
            </div>
          </div>
          <button onClick={save} className="btn-primary w-full justify-center flex items-center gap-2"><Save size={14} /> Guardar</button>
        </div>
      ) : (
        <>
          {content && <p className="text-sm text-[#0A0A0A] leading-relaxed">{content}</p>}
          {items?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {items.map((it, i) => (
                <div key={i} className="inline-flex items-center gap-2">
                  {isColors && it.hex ? (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#E5E5E5]">
                      <div className="w-4 h-4 rounded" style={{ background: it.hex }} />
                      <span className="text-xs font-medium">{it.name}</span>
                      <span className="text-[10px] font-mono text-[#666]">{it.hex}</span>
                    </div>
                  ) : (
                    <span className="pill pill-gray">{it.name}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const NewSection = ({ onClose, onCreated }) => {
  const [section, setSection] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!section.trim() || !title.trim()) return;
    setSaving(true);
    try {
      await api.post("/brand-kit", { section, title, content, items: [] });
      toast.success("Sección creada");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-7 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <div className="label-overline">Brand Kit</div>
            <h2 className="font-display text-2xl font-medium">Nueva sección</h2>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div>
          <label className="aura-label">Identificador</label>
          <input required className="aura-input" value={section} onChange={(e) => setSection(e.target.value)} placeholder="ej: tono_premium" />
        </div>
        <div>
          <label className="aura-label">Título</label>
          <input required className="aura-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tono premium" />
        </div>
        <div>
          <label className="aura-label">Contenido</label>
          <textarea className="aura-input" rows="3" value={content} onChange={(e) => setContent(e.target.value)} />
        </div>
        <button type="submit" disabled={saving} className="btn-primary w-full justify-center flex items-center gap-2">
          {saving && <span className="spinner" />} Crear
        </button>
      </form>
    </div>
  );
};

export default BrandKit;
